import { createAction, type QueuedAction } from "./actionTypes";
import { durationToUnitsSeconds, formatEntryId } from "./entryId";
import {
	type ActionDeps,
	actingHeaders,
	apiCall,
	combineRollback,
	refreshBoth,
} from "./types";

export type EditPayload = {
	id: number;
	entryGroupId?: string | null;
	date: string;
	matter: string;
	matter_id: string;
	description: string;
	duration: number;
	task: string;
	taskCodeId?: string | null;
	activity: string;
	activityCodeId?: string | null;
	timekeeperId?: string | null;
};

/**
 * Map the form payload to the v2 PATCH wire shape. Empty strings become null
 * (the v2 merge-patch convention for "clear this field"). `id` is in the URL,
 * not the body; `matter` is display-only and not sent.
 */
export function toUpdateBody(entry: EditPayload) {
	return {
		date: entry.date,
		unitsSeconds: durationToUnitsSeconds(entry.duration),
		description: entry.description,
		matterId: entry.matter_id || null,
		taskId: entry.task || null,
		activityId: entry.activity || null,
	};
}

export function toUpdateEntryGroupBody(entry: EditPayload) {
	return {
		narrative: entry.description,
		durationSeconds: durationToUnitsSeconds(entry.duration),
		matterId: entry.matter_id || null,
		...(entry.taskCodeId !== undefined ? { taskCodeId: entry.taskCodeId } : {}),
		...(entry.activityCodeId !== undefined
			? { activityCodeId: entry.activityCodeId }
			: {}),
	};
}

export function editAction(entry: EditPayload, deps: ActionDeps): QueuedAction {
	const rollbackLegacy = deps.updateEntries((prev) =>
		prev.map((e) => (e.id === entry.id ? { ...e, ...entry } : e)),
	);
	// Mirror the optimistic edit into the v2 cache when present. The wire
	// shape is different (camelCase, nested matter/codes), so we map the
	// subset the EditPayload actually carries. Fields the payload doesn't
	// touch (matter description, application, billable, etc.) stay as-is.
	const entryV2Id = formatEntryId(entry.id);
	const rollbackV2 =
		deps.updateEntryGroups?.((prev) =>
			prev.map((e) => {
				if (e.id !== entryV2Id) return e;
				return {
					...e,
					description: entry.description,
					units: entry.duration,
					unitsSeconds: durationToUnitsSeconds(entry.duration),
					matter: e.matter
						? { ...e.matter, id: entry.matter_id || null }
						: entry.matter_id
							? {
									id: entry.matter_id,
									code: null,
									description: entry.matter || null,
									client: null,
								}
							: null,
					codes: {
						task: entry.task || null,
						activity: entry.activity || null,
					},
				};
			}),
		) ?? null;

	return createAction({
		execute: () =>
			apiCall(deps, {
				endpoint: `/v2/entries/${entryV2Id}`,
				method: "PATCH",
				body: toUpdateBody(entry),
				headers: actingHeaders(entry.timekeeperId),
			}),
		rollback: combineRollback(rollbackLegacy, rollbackV2),
		toast: {
			loading: "Saving changes…",
			success: "Entry saved",
			error: "Failed to save entry",
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
